import { UserRole } from '@/types/api';
import { normalizeRole } from '@/utils/roleUtils';

/**
 * Which parts of Email Management each role can actually use.
 *
 * BugHerd #158: the navigation item and all four routes were gated on "Agent or higher",
 * but the API is not. An Agent landed on the Settings tab and received 403 from settings,
 * templates and stats; a Manager received 403 from every tab. The page advertised
 * capabilities the server refuses.
 *
 * This table mirrors the [Authorize(Roles = ...)] attributes on
 * src/backend/SupportDesk.Api/Controllers/EmailController.cs as at 01/09/2026:
 *
 *   GET /email/settings             Admin
 *   GET /email/templates            Admin, Manager
 *   GET /email/notifications/stats  Admin, Manager
 *   GET /email/notifications/history Admin, Agent
 *   GET /email/notifications/logs   Admin
 *
 * The server is the authority and is unchanged; this only stops the UI offering what the
 * server will refuse. If those attributes change, change this table with them - it is the
 * single place the mapping is expressed.
 *
 * Note the roles are matched by exact value, not with hasMinimumRole(). "Agent or higher"
 * is what produced this defect: it silently includes Manager and Admin, whose permissions
 * on these endpoints are different again.
 */

export type EmailTabId = 'settings' | 'history' | 'logs';

/** Roles permitted on the endpoint backing each tab. */
const TAB_ROLES: Record<EmailTabId, UserRole[]> = {
  settings: [UserRole.Admin],
  history: [UserRole.Admin, UserRole.Agent],
  logs: [UserRole.Admin],
};

/** Tab order as presented in the UI; the first accessible one is the landing tab. */
const TAB_ORDER: EmailTabId[] = ['settings', 'history', 'logs'];

const STATS_ROLES: UserRole[] = [UserRole.Admin, UserRole.Manager];
const TEMPLATE_ROLES: UserRole[] = [UserRole.Admin, UserRole.Manager];

/** Tabs this role can open without the request behind them failing. */
export function getAccessibleEmailTabs(role: unknown): EmailTabId[] {
  const normalised = normalizeRole(role);
  return TAB_ORDER.filter((tab) => TAB_ROLES[tab].includes(normalised));
}

/** The tab a role should land on, or null when none of them are usable. */
export function getDefaultEmailTab(role: unknown): EmailTabId | null {
  return getAccessibleEmailTabs(role)[0] ?? null;
}

export function canAccessEmailTab(role: unknown, tab: EmailTabId): boolean {
  return getAccessibleEmailTabs(role).includes(tab);
}

/**
 * Whether to offer Email Management in navigation at all. A role with no usable tab is not
 * "authorised for every child surface", so it is not offered the entry point.
 */
export function canSeeEmailManagementNav(role: unknown): boolean {
  return getAccessibleEmailTabs(role).length > 0;
}

/** Header statistics come from /email/notifications/stats. */
export function canViewEmailStats(role: unknown): boolean {
  return STATS_ROLES.includes(normalizeRole(role));
}

/** Header template count comes from /email/templates. */
export function canViewEmailTemplates(role: unknown): boolean {
  return TEMPLATE_ROLES.includes(normalizeRole(role));
}
